import { isRecord } from '@/utils/objects';
import type { ConditionNode, ParseOptions, UnknownNode } from '../types';
import { parseGroup } from './parse-group';
import { parseAttribute } from './parse-attribute';
import { parseEvent } from './parse-event';
import { parseSegment } from './parse-segment';
import { parseDevice } from './parse-device';
import { parsePageview } from './parse-pageview';
import { parseScreenview } from './parse-screenview';
import { parseForm } from './parse-form';
import { parseMessage } from './parse-message';
import { parseInbound } from './parse-inbound';
import { parseOptOut } from './parse-opt-out';
import { parseRelationship } from './parse-relationship';
import { parseForeignEvent } from './parse-wait-until';

const MAX_DEPTH = 10;

/**
 * Ordered list of parsers. Each returns a typed node or null.
 * Order matters: more specific parsers (form, relationship) run before
 * general ones (event, attribute) to avoid false matches.
 */
const PARSERS: Array<(raw: Record<string, unknown>, options: ParseOptions) => ConditionNode | null> = [
  parseForeignEvent,
  parseRelationship,
  parseSegment,
  parseOptOut,
  parseForm,
  parseDevice,
  parsePageview,
  parseScreenview,
  parseInbound,
  parseMessage,
  parseEvent,
  parseAttribute,
];

function looksLikeRelationshipGroup(raw: Record<string, unknown>): boolean {
  if (!('and' in raw) || !Array.isArray(raw.and)) return false;
  return raw.and.some(
    (item) =>
      item != null &&
      typeof item === 'object' &&
      !Array.isArray(item) &&
      'relationship' in item &&
      typeof item.relationship === 'object' &&
      item.relationship != null,
  );
}

function parseOne(raw: Record<string, unknown>, depth: number, options: ParseOptions): ConditionNode {
  if (depth > MAX_DEPTH) {
    return { type: 'unknown', raw } satisfies UnknownNode;
  }

  // Special case: { and: [{ relationship }, ...filters] } is a relationship, not a group
  if (looksLikeRelationshipGroup(raw)) {
    const relationshipNode = parseRelationship(raw);
    if (relationshipNode != null) return relationshipNode;
  }

  // Try group (and/or)
  const group = parseGroup(raw, (child) => parseOne(child, depth + 1, options));
  if (group != null && group.children.length > 0) return group;

  // Try each typed parser
  for (const parser of PARSERS) {
    const node = parser(raw, options);
    if (node != null) return node;
  }

  return { type: 'unknown', raw } satisfies UnknownNode;
}

/**
 * Parse raw condition JSON (from audit logs or segment data) into a typed
 * ConditionNode tree.
 *
 * Accepts any shape: { and: [...] }, { segment: { id } }, { field, operator, value }, etc.
 * Returns an UnknownNode for anything it can't parse.
 */
export function parseConditions(json: unknown, options: ParseOptions = {}): ConditionNode {
  if (!isRecord(json)) {
    return { type: 'unknown', raw: {} } satisfies UnknownNode;
  }
  return parseOne(json, 0, options);
}
